import { useState, type MouseEvent } from 'react';
import LogoutIcon from '@mui/icons-material/Logout';
import { Trans } from '@lingui/react/macro';
import { useAuth } from '@/features/auth/components/auth-context/AuthContext.tsx';

import Typography from '@mui/material/Typography';
import Avatar from '@mui/material/Avatar';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';

export function UserMenu() {
  const theme = useTheme();
  const { logout, currentUser } = useAuth();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    handleClose();
  };

  const initials = currentUser.name
    .split(' ')
    .slice(0, 2)
    .map((n) => n[0].toUpperCase())
    .join('');

  const avatar = (
    <Avatar
      sx={{ width: 32, height: 32, fontSize: 15, backgroundColor: theme.palette.secondary.dark }}
    >
      {initials}
    </Avatar>
  );

  return (
    <Stack direction="row" spacing={1} alignItems="baseline">
      <Typography variant="body1" sx={{ display: { xs: 'none', sm: 'inline' } }}>
        {currentUser.name}
      </Typography>
      <IconButton
        onClick={handleClick}
        aria-controls={open ? 'user-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        aria-label="user menu"
      >
        {avatar}
      </IconButton>
      <Menu
        id="user-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <Stack px={2} spacing={2} alignItems="center" direction="row" role="menuitem">
          {avatar}

          <Stack>
            <Typography>{currentUser.name}</Typography>
            <Typography variant="body2" color="textSecondary">
              {currentUser.email}
            </Typography>
          </Stack>
        </Stack>
        <Divider sx={{ my: 1 }} />
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <Trans>Logout</Trans>
        </MenuItem>
      </Menu>
    </Stack>
  );
}
